import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getTokens } from "../apis-tracker";
import { useAppProvider } from "../AppContext";
import {
  Cat20Utxo,
  MempoolUtxoProvider,
  singleSend,
  toTokenAddress,
  UnisatSigner,
} from "@cat-protocol/cat-sdk";
import {
  getExplorerServiceUrl,
  getTxStatus,
  patchGetUtxos,
  TokenItem,
  TxStatus,
} from "../api";

import { useInterval } from "usehooks-ts";
import { StorageAPI } from "../storage";
import MyChainProvider from "../MyChainProvider";
import { unScaleByDecimals } from "../utils";
import { SafnectWallet } from "../types";
import { SafnectSigner } from "../safnectSigner";

interface SwapDialogProps {
  tokenItem: TokenItem;
  handleClose: () => void;
}

enum UpgradeStatus {
  FETCH_TOKEN_UTXO,
  NO_UTXO,
  REQUEST_SIGN,
  SENT,
  SUCCESS,
  FAILED,
}

function getStatusText(status: UpgradeStatus) {
  switch (status) {
    case UpgradeStatus.FETCH_TOKEN_UTXO:
      return "Fetching token utxo ...";
    case UpgradeStatus.NO_UTXO:
      return "No cat20 utxo";
    case UpgradeStatus.REQUEST_SIGN:
      return "Requesting sign ...";
    case UpgradeStatus.SENT:
      return "Sent";
    case UpgradeStatus.SUCCESS:
      return "Upgrade successful";
    case UpgradeStatus.FAILED:
      return "Failed";
  }
}

function calcAmount(utxos: Array<Cat20Utxo>) {
  return utxos.reduce((acc, utxo) => (acc += utxo.state.amount), BigInt(0));
}

function amount2Str(amount: bigint, decimals: number) {
  return unScaleByDecimals(amount.toString().replace('n', ''), decimals)
}

export default function SwapDialog(props: SwapDialogProps) {
  const { connected, connectedAccount, network, feeRate, connectedWallet } = useAppProvider();

  const [_error, setError] = useState("");

  const [_swapTxId, setSwapTxId] = useState("");

  const [_cat20Utxos, setCat20Utxos] = useState<Array<Cat20Utxo>>([]);
  const [_totalUtxoCount, setTotalUtxoCount] = useState<number>(0);


  const [_status, setStatus] = useState(UpgradeStatus.FETCH_TOKEN_UTXO);

  const { tokenItem } = props;

  useEffect(() => {
    if (connectedAccount) {
      const address = connectedAccount!.payment.address;
      setStatus(UpgradeStatus.FETCH_TOKEN_UTXO);
      getTokens(true, network, tokenItem.tokenId, address)
        .then((cat20Utxos) => {
          setTotalUtxoCount(cat20Utxos.length);
          setCat20Utxos(cat20Utxos.slice(0, 4));
        })
        .catch((e) => {
          console.error("getTokens error", e);
          setStatus(UpgradeStatus.NO_UTXO);
        });
    }
  }, [tokenItem, connectedAccount, network, tokenItem.tokenId]);

  useEffect(() => {
    const UpgradeCoin = async (upgradeAddress: string) => {
      try {
        const address = connectedAccount!.payment.address;

        const chainProvider = new MyChainProvider(address, tokenItem.tokenId, network);
        const utxoProvider = new MempoolUtxoProvider(network);
        patchGetUtxos(utxoProvider)

        const amount = calcAmount(_cat20Utxos);

        const signer = connectedWallet === SafnectWallet ? new SafnectSigner() : new UnisatSigner(window.unisat);

        setStatus(UpgradeStatus.REQUEST_SIGN);
        const sendRes = await singleSend(
          signer,
          utxoProvider,
          chainProvider,
          tokenItem.minterAddr,
          _cat20Utxos,
          [
            {
              address: toTokenAddress(upgradeAddress),
              amount,
            },
          ],
          toTokenAddress(address),
          feeRate
        );

        console.log("sendRes: ", sendRes);

        setSwapTxId(sendRes.sendTxId);

        setStatus(UpgradeStatus.SENT);

        StorageAPI.addOrUpdateHistoryItem(network, {
          type: 'CAT20',
          amountOrLocalId: amount2Str(amount, tokenItem.metadata.decimals),
          symbol: tokenItem.metadata.symbol,
          txId: sendRes.sendTxId,
          status: TxStatus.initial,
        });
      } catch (error) {
        console.error("swapCoin error", error);
        if (error instanceof Error) {
          console.error("setError error", error);
          setError((error as Error).message);
        } else if (typeof error === "string") {
          console.error("adafdsf error", error);
          setError(String(error));
        } else if (error && typeof error === "object" && "message" in error) {
          console.error("error", error);
          setError((error as any).message);
        } else {
          console.error("error", error);
          setError("unknown error");
        }
      }
    };

    if (connected() && _cat20Utxos.length > 0) {
      UpgradeCoin(tokenItem.paymentAddr);
    }
  }, [connected, connectedWallet, network, tokenItem, feeRate, connectedAccount, _cat20Utxos]);

  useInterval(
    () => {
      // Your custom logic here

      getTxStatus(_swapTxId, network)
        .then((status) => {
          StorageAPI.updateHistoryItemTxStatus(network, _swapTxId, status);
          if (status === TxStatus.finished) {
            setStatus(UpgradeStatus.SUCCESS);
          } else if (status === TxStatus.failed) {
            setStatus(UpgradeStatus.FAILED);
          }
        })
        .catch((e) => {
          console.error("getTxStatus:", e);
        });
    },
    // Delay in milliseconds or null to stop it
    _status === UpgradeStatus.SENT && _swapTxId ? 5000 : null
  );

  return (
    <React.Fragment>
      <Dialog
        sx={{ zIndex: 1800 }}
        disableEscapeKeyDown={true}
        open={true}
        fullWidth
        //onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Upgrade {tokenItem.metadata.symbol} Tokens
        </DialogTitle>
        <DialogContent>
          <Box>
            {_error ? (
              <DialogContentText id="alert-dialog-error" color="red" mt={1}>
                {`Error: ${_error}`}
              </DialogContentText>
            ) : (
              <DialogContentText id="alert-dialog-description" mt={1}>
                {_status === UpgradeStatus.SENT && _swapTxId ? (
                  <>
                    {`Sending ${amount2Str(calcAmount(_cat20Utxos), tokenItem.metadata.decimals)} ${
                      tokenItem.metadata.symbol
                    } tokens to CAT Protocol Upgrade in tx:`}
                    <Link
                      href={`${getExplorerServiceUrl(network)}/tx/${_swapTxId}`}
                      rel="noopener noreferrer"
                      target="_blank"
                      ml={1}
                      variant="body2"
                    >
                      {_swapTxId?.substring(0, 6) + "..."}
                    </Link>
                  </>
                ) : (
                  getStatusText(_status)
                )}
              </DialogContentText>
            )}
          </Box>

          {_status === UpgradeStatus.SENT ?  <CircularProgress size={26} sx={{mt: 1}}></CircularProgress> : <></>}
          <DialogContentText id="alert-dialog-description" color="grey" mt={1}>
            { _totalUtxoCount <= 4 ? (
              `This upgrade involves ${amount2Str(calcAmount(_cat20Utxos), tokenItem.metadata.decimals)} ${
                tokenItem.metadata.symbol
              } tokens.`
            ) : (`This upgrade involves only ${amount2Str(calcAmount(_cat20Utxos), tokenItem.metadata.decimals)} ${
                tokenItem.metadata.symbol
              } tokens spread across 4 UTXOs. To upgrade all your tokens in a single transaction, please use the Unisat wallet to merge all your UTXOs first.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
